import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useMutation } from 'react-query';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme, type Theme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { Field, RichText, RichTextField, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { AppDispatch, RootState } from '../../store/store';
import { setAretiChatbotLoaded } from '../../features/aretiSectionSlice';
import { setConversationId } from '../../features/cdpDataSlice';
import { post } from '../../lib/utils/axiosFetcher';
import { post as cdpPost } from '../../lib/utils/axiosCDPFetcher';
import { transformDataToSaveMain } from '../../transformers/prescreener.transform';
import { ARETI_DEFAULT_SCREENER_TYPE, JOURNEY_STATUS, SCREENER_STATUS } from '../../constants';
import { getEnrollmentId } from '../../lib/utils/enrollmentUtil';
import {
  ARETI_WIDGET_DOM_ID,
  ARETI_WIDGET_MAX_WIDTH_PX,
  AretiWidgetConfig,
  openAretiChatbot,
} from '../../lib/aretiChatbot';
import { ComponentProps } from '../../lib/component-props';
import useApplyV2Theme from '../../lib/hooks/useApplyV2Theme';
import { parseConversationSidFromLocalStorage } from '../../lib/utils/aretiConversationStorage';
import { logger } from '../../logger';
import UiHeading from '../shared/Heading';

const ARETI_HEALTH_SCREENER_ID = 'areti-health-screener';
const DEFAULT_SECTION_BACKGROUND_COLOR = '#ECF4F8';
const ARETI_CONVERSATION_STORAGE_KEY =
  process.env.ARETI_CONVERSATION_STORAGE_KEY?.trim() || 'TWILIO_WEBCHAT_WIDGET';

const parsedPollIntervalMs = Number(process.env.REACT_POLL_TEST_INTERVAL_MS);
const REACT_POLL_TEST_INTERVAL_MS =
  Number.isFinite(parsedPollIntervalMs) && parsedPollIntervalMs > 0 ? parsedPollIntervalMs : 2500;

type AretiHealthScreenerProps = ComponentProps & {
  fields: {
    title: Field<string>;
    content: RichTextField;
    sectionBackgroundColor?: Field<string>;
    headerContent?: Field<string>;
    footerContent?: Field<string>;
    enableZipcode?: Field<boolean>;
    screenerId?: Field<string>;
    color?: Field<string>;
    studyName?: Field<string>;
    actionType?: Field<string>;
    sourceType?: Field<string>;
    site?: Field<string>;
    study?: Field<string>;
  };
};

/**
 * Left: Title (H2) + RTE content. Right: Study Companion (vendor).
 * `/sitecore/templates/Feature/Areti/AretiContent` (+ AretiConfiguration). Same props pattern as DTPHeroBanner (`props?.fields`).
 */
const AretiHealthScreener = (props: AretiHealthScreenerProps): JSX.Element => {
  const theme = useTheme();
  const { isToapplyV2Theme } = useApplyV2Theme();
  const { sitecoreContext } = useSitecoreContext();
  const siteSettings: any = sitecoreContext?.siteSettings;
  const tenantKey = siteSettings?.tenantKey?.value || '';

  const cdpState = useSelector((state: RootState) => state.cdpData);

  const potentialParticipantDetailsFromStore = useSelector(
    (state: RootState) => state.potentialParticipantDetails
  );
  const conversationId = useSelector((state: RootState) => state.cdpData?.conversationId);

  const dispatch = useDispatch<AppDispatch>();
  const isChatbotLoaded = useSelector(
    (state: RootState) => state.aretiSection?.isChatbotLoaded ?? false
  );

  const onChatbotLoaded = useCallback(() => {
    dispatch(setAretiChatbotLoaded(true));
  }, [dispatch]);

  const aretiOpenedForKeyRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    const enrollmentId = potentialParticipantDetailsFromStore?.enrollmentId;
    const guestRef = potentialParticipantDetailsFromStore?.potentialParticipant_ID;
    if (!enrollmentId || !guestRef) {
      return;
    }

    const f = props.fields;
    const header = f?.headerContent?.value;
    const footer = f?.footerContent?.value;
    const enableZipcode = f?.enableZipcode?.value;
    const screenerId = f?.screenerId?.value;
    const color = f?.color?.value;
    const studyName = f?.studyName?.value;
    const actionType = f?.actionType?.value;
    const sourceType = f?.sourceType?.value;
    const site = f?.site?.value;
    const study = f?.study?.value;

    const override: Partial<AretiWidgetConfig> = {};
    if (header) override.header = String(header);
    if (footer) override.footer = String(footer);
    if (typeof enableZipcode === 'boolean') override.withZipCode = enableZipcode;
    if (actionType) override.actionType = String(actionType);
    if (color) override.color = String(color);
    if (studyName) override.studyName = String(studyName);

    const optionsOverride: Partial<AretiWidgetConfig['options']> = {};
    if (screenerId) optionsOverride.deployment_id = String(screenerId);
    if (sourceType) optionsOverride.source_type = String(sourceType);
    if (site) optionsOverride.site = String(site);
    if (study) optionsOverride.study = String(study);
    optionsOverride.customer_payload = {
      guestRef: String(guestRef),
      enrollmentId: String(enrollmentId),
    };
    if (Object.keys(optionsOverride).length > 0) {
      override.options = optionsOverride as AretiWidgetConfig['options'];
    }
    const aretiConfigValues = Object.keys(override).length ? override : undefined;

    const deploymentId = aretiConfigValues?.options?.deployment_id ?? '';
    const dedupeKey = `${enrollmentId}:${guestRef}:${deploymentId}`;
    if (aretiOpenedForKeyRef.current === dedupeKey) {
      return;
    }
    aretiOpenedForKeyRef.current = dedupeKey;
    openAretiChatbot(aretiConfigValues, onChatbotLoaded);
  }, [
    potentialParticipantDetailsFromStore?.enrollmentId,
    potentialParticipantDetailsFromStore?.potentialParticipant_ID,
  ]);

  const updateScreenerDetailsinCDP = async (
    cdpBody: Record<string, unknown>,
    guestType: string
  ) => {
    const MAX_EXPERIENCE_RETRY_COUNT = Number(process.env.RETRY_COUNT ?? 4);
    let retryattempt = 0;
    while (retryattempt < MAX_EXPERIENCE_RETRY_COUNT) {
      retryattempt += 1;
      const guestRefId = potentialParticipantDetailsFromStore?.potentialParticipant_ID;
      if (!guestRefId) {
        logger.error('AddPrescreenerToDataExtension skipped: potentialParticipant_ID is missing', {
          guestType,
        });
        return;
      }
      const URL = `/api/contacts/AddPrescreenerToDataExtension/${guestRefId}?guestType=${guestType}`;
      try {
        const response = await cdpPost(URL, cdpBody);
        if (response?.status === 200) {
          return;
        }
        if (retryattempt >= MAX_EXPERIENCE_RETRY_COUNT) {
          logger.error('Max retry failed for CDP call', {
            error: 'CDP fetch failed:' + response,
            guestRefId,
          });
          throw new Error('AddPrescreenerToDataExtension failed: non-200 status');
        }
      } catch (_e) {
        if (retryattempt >= MAX_EXPERIENCE_RETRY_COUNT) {
          logger.error(`Max retry failed with exception`, {
            error: _e,
            guestRefId,
          });
          throw new Error('AddPrescreenerToDataExtension failed');
        }
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }
    throw new Error('AddPrescreenerToDataExtension: exhausted retries without success');
  };

  const saveSurveyMain = async (
    screenerID: string
  ): Promise<Record<string, unknown> | undefined> => {
    if (potentialParticipantDetailsFromStore) {
      const { potentialParticipant_ID, potentialId } = potentialParticipantDetailsFromStore;
      const transformedData = transformDataToSaveMain(
        potentialParticipant_ID,
        potentialId,
        String(screenerID),
        conversationId,
        siteSettings?.screenerType?.fields?.name?.value || ARETI_DEFAULT_SCREENER_TYPE,
        tenantKey,
        getEnrollmentId() || ''
      );
      const { data: response } = await post('/Prescreener/Add', transformedData);
      response['screenerResponseID'] = screenerID;
      return response;
    }
    return;
  };

  const { mutate: savePrescreenerMain } = useMutation<
    Record<string, unknown> | undefined,
    Error,
    string
  >(saveSurveyMain, {
    retry: process.env.RETRY_COUNT ? Number(process.env.RETRY_COUNT) : 4,
    onSuccess: async (data) => {
      try {
        if (data?.screenerResponseID) {
          const cdpBody = {
            participantType: null,
            journeyStep: '',
            screenerId: String(data?.screenerResponseID),
            screenerResponseID: conversationId,
            patientReferralStatus: cdpState?.patientReferralStatus,
            screenerType:
              siteSettings?.screenerType?.fields?.name?.value || ARETI_DEFAULT_SCREENER_TYPE,
            screenerStatus: SCREENER_STATUS.DISQUALIFIED,
            screenerCompleted: 'false',
            screenerOutcomeParams: [] as { key: string; value: string }[],
            JourneyStatus: JOURNEY_STATUS.PARTIAL_EXTERNAL_SCREENER,
            zipCode: '',
            returningUserRequestModel: {},
          };
          await updateScreenerDetailsinCDP(cdpBody, cdpState?.guestType || 'visitor');
        }
      } catch (_e: any) {
        console.error('Error updating screener details in CDP:', _e);
      }
    },
    onError: async () => undefined,
    onSettled: async () => {
      //   queryClient.invalidateQueries(''); // invalidate any get query for screener data
    },
  });

  const lastConversationSidRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (conversationId) {
      return;
    }
    let intervalId: number | undefined;
    const stopPolling = () => {
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
        intervalId = undefined;
      }
    };
    const poll = (): void => {
      let raw: string | null;
      try {
        raw = window.localStorage.getItem(ARETI_CONVERSATION_STORAGE_KEY);
      } catch (error) {
        console.error('Failed to access localStorage:', error);
        return;
      }
      const conversationSid = parseConversationSidFromLocalStorage(raw);
      if (conversationSid) {
        dispatch(setConversationId(conversationSid));
        stopPolling();
      }
      if (lastConversationSidRef.current === undefined) {
        lastConversationSidRef.current = conversationSid;
        if (conversationSid !== null) {
          dispatch(setConversationId(conversationSid));
          stopPolling();
        }
      } else if (conversationSid !== lastConversationSidRef.current) {
        lastConversationSidRef.current = conversationSid;
        dispatch(setConversationId(conversationSid));
        if (conversationSid !== null) {
          stopPolling();
        }
      }
    };
    poll();
    intervalId = window.setInterval(poll, REACT_POLL_TEST_INTERVAL_MS);
    return () => stopPolling();
  }, [conversationId, dispatch]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!conversationId) return;

    const { potentialParticipant_ID, potentialId } = potentialParticipantDetailsFromStore ?? {};
    const hasPotentialId = potentialId !== '' && potentialId !== undefined && potentialId !== null;
    if (!potentialParticipant_ID || !hasPotentialId) return;

    const screenerID = props?.fields?.screenerId?.value;
    if (!screenerID) return;

    const potentialIdNum = typeof potentialId === 'number' ? potentialId : Number(potentialId);
    if (!Number.isFinite(potentialIdNum)) {
      console.error('Invalid potentialId for prescreener:', potentialId);
      return;
    }

    savePrescreenerMain(String(screenerID));
  }, [conversationId]);

  const sectionBg =
    props?.fields?.sectionBackgroundColor?.value?.trim() || DEFAULT_SECTION_BACKGROUND_COLOR;
  const titleText = props?.fields?.title?.value;
  const hasContent = Boolean(props?.fields?.content?.value);
  const hasLeftCopy = Boolean(titleText?.trim()) || hasContent;

  return (
    <Box
      component="section"
      id={ARETI_HEALTH_SCREENER_ID}
      aria-busy={!isChatbotLoaded}
      aria-live="polite"
      sx={{
        position: 'relative',
        width: '100%',
        bgcolor: sectionBg,
        py: { xs: 3, sm: 3.5, md: 4 },
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      {!isChatbotLoaded && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: sectionBg,
            zIndex: 1,
          }}
        >
          <CircularProgress aria-label="Loading Study Companion" />
        </Box>
      )}
      <Box
        sx={{
          maxWidth: (t: Theme) => `${t.breakpoints.values.xl}px`,
          mx: 'auto',
          px: { xs: 0, sm: 0 },
        }}
      >
        <Grid
          container
          columnSpacing={{ xs: 0, md: hasLeftCopy ? 2 : 0 }}
          rowSpacing={{ xs: 2, md: 0 }}
          alignItems="stretch"
          justifyContent={hasLeftCopy ? undefined : 'center'}
          sx={{ flexGrow: 1 }}
        >
          {hasLeftCopy && (
            <Grid
              className="content-left"
              item
              xs={12}
              md={12}
              lg={6}
              xl={7}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: { xs: 'flex-start', md: 'flex-start' },
                minHeight: { xs: 0, md: theme.spacing(40) },
                minWidth: 0,
                pr: { xs: 0, md: 1 },
                mt: { xs: 0, lg: 5 },
                gap: titleText && hasContent ? theme.spacing(1.5) : 0,
              }}
            >
              <Box
                sx={{
                  pl: { xs: 0, md: 0, lg: '9%', xl: '11%' },
                  display: 'flex',
                  flexDirection: 'column',
                  flexGrow: 1,
                  '@media (min-width:1150px) and (max-width:1299.95px)': {
                    pl: '16%',
                  },
                }}
              >
                {titleText &&
                  (isToapplyV2Theme ? (
                    <UiHeading size="h2" color="primary" align="left">
                      {props?.fields?.title}
                    </UiHeading>
                  ) : (
                    <Typography
                      variant="h2"
                      component="h2"
                      sx={{
                        color: (t) => t.palette.themeColor.main,
                        fontSize: '1.5rem',
                        marginBottom: theme.spacing(1),
                      }}
                    >
                      <Text field={props?.fields?.title} />
                    </Typography>
                  ))}
                {props?.fields?.content && <RichText field={props?.fields.content} />}
              </Box>
            </Grid>
          )}

          <Grid
            className="content-right"
            item
            xs={12}
            md={hasLeftCopy ? 12 : 12}
            lg={hasLeftCopy ? 6 : 12}
            xl={hasLeftCopy ? 5 : 12}
            sx={{
              display: 'flex',
              justifyContent: hasLeftCopy
                ? { xs: 'center', md: 'center', lg: 'flex-start' }
                : 'center',
              alignItems: { xs: 'stretch', md: 'flex-start' },
              marginTop: { xs: 2, md: 5 },
              minWidth: 0,
              overflow: 'visible',
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: {
                  xs: '100%',
                  sm: `min(100%, ${ARETI_WIDGET_MAX_WIDTH_PX}px)`,
                },
              }}
            >
              <Box
                component="div"
                id={ARETI_WIDGET_DOM_ID}
                sx={{
                  m: 0,
                  minHeight: theme.spacing(40),
                  width: '100%',
                  maxWidth: '100%',
                  overflow: 'visible',
                }}
                aria-label="Study Companion"
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default AretiHealthScreener;
